import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import MetricsCard from './MetricsCard';

const PushNotificationPanel = () => {
  const [notifications, setNotifications] = useState([
    {
      id: 1,
      title: 'New Wedding Season Offers',
      message: 'Check out exclusive discounts from top vendors',
      targetAudience: 'all-users',
      scheduled: '2025-07-12T18:00:00Z',
      status: 'scheduled',
      sentCount: 0,
      clickRate: 0
    },
    {
      id: 2,
      title: 'Profile Completion Reminder',
      message: 'Complete your profile to get better matches',
      targetAudience: 'incomplete-profiles',
      scheduled: '2025-07-12T15:30:00Z',
      status: 'sent',
      sentCount: 245,
      clickRate: 8.2
    },
    {
      id: 3,
      title: 'Vendor Verification Required',
      message: 'Complete your verification to start receiving bookings',
      targetAudience: 'pending-vendors',
      scheduled: '2025-07-12T14:00:00Z',
      status: 'sent',
      sentCount: 23,
      clickRate: 15.5
    }
  ]);

  const [newNotification, setNewNotification] = useState({
    title: '',
    message: '',
    targetAudience: 'all-users',
    scheduled: '',
    actionUrl: ''
  });

  const notificationStats = {
    totalSent: notifications.reduce((sum, n) => sum + n.sentCount, 0),
    scheduled: notifications.filter(n => n.status === 'scheduled').length,
    avgClickRate: notifications.filter(n => n.status === 'sent').reduce((sum, n) => sum + n.clickRate, 0) / notifications.filter(n => n.status === 'sent').length || 0,
    activeUsers: 1245
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'sent': return 'text-green-600 bg-green-100';
      case 'scheduled': return 'text-blue-600 bg-blue-100';
      case 'draft': return 'text-gray-600 bg-gray-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const handleSendNotification = (notificationId) => {
    setNotifications(prev => prev.map(notification => 
      notification.id === notificationId 
        ? { ...notification, status: 'sent', sentCount: Math.floor(Math.random() * 500) + 100 }
        : notification
    ));
  };

  const handleCreateNotification = () => {
    if (newNotification.title && newNotification.message) {
      const notification = {
        id: Date.now(),
        ...newNotification,
        status: newNotification.scheduled ? 'scheduled' : 'draft',
        sentCount: 0,
        clickRate: 0
      };
      setNotifications(prev => [notification, ...prev]);
      setNewNotification({
        title: '',
        message: '',
        targetAudience: 'all-users',
        scheduled: '',
        actionUrl: ''
      });
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-purple-700 rounded-xl p-6 text-white">
        <h2 className="font-heading font-bold text-2xl mb-2">
          Push Notification System
        </h2>
        <p className="text-purple-100">
          Send targeted notifications to users and vendors
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <MetricsCard
          title="Total Sent"
          value={notificationStats.totalSent.toLocaleString()}
          icon="Send"
          color="blue"
        />
        <MetricsCard
          title="Scheduled"
          value={notificationStats.scheduled}
          icon="Clock"
          color="orange"
        />
        <MetricsCard
          title="Avg Click Rate"
          value={`${notificationStats.avgClickRate.toFixed(1)}%`}
          icon="MousePointer"
          color="green"
        />
        <MetricsCard
          title="Active Users"
          value={notificationStats.activeUsers.toLocaleString()}
          icon="Users"
          color="purple"
        />
      </div>

      {/* Create New Notification */}
      <div className="bg-white rounded-xl shadow-soft p-6">
        <h3 className="font-heading font-semibold text-xl text-text-primary mb-6">
          Create New Notification
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Title
            </label>
            <input
              type="text"
              value={newNotification.title}
              onChange={(e) => setNewNotification(prev => ({ ...prev, title: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              placeholder="Enter notification title"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Target Audience
            </label>
            <select
              value={newNotification.targetAudience}
              onChange={(e) => setNewNotification(prev => ({ ...prev, targetAudience: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
            >
              <option value="all-users">All Users</option>
              <option value="incomplete-profiles">Incomplete Profiles</option>
              <option value="pending-vendors">Pending Vendors</option>
              <option value="active-vendors">Active Vendors</option>
              <option value="premium-users">Premium Users</option>
            </select>
          </div>
          
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Message
            </label>
            <textarea
              value={newNotification.message}
              onChange={(e) => setNewNotification(prev => ({ ...prev, message: e.target.value }))}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              placeholder="Enter notification message"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Schedule (Optional)
            </label>
            <input
              type="datetime-local"
              value={newNotification.scheduled}
              onChange={(e) => setNewNotification(prev => ({ ...prev, scheduled: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Action URL (Optional)
            </label>
            <input
              type="url"
              value={newNotification.actionUrl}
              onChange={(e) => setNewNotification(prev => ({ ...prev, actionUrl: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              placeholder="https://example.com"
            />
          </div>
        </div>
        
        <div className="flex justify-end mt-6 space-x-3">
          <Button variant="outline">Save as Draft</Button>
          <Button variant="primary" onClick={handleCreateNotification}>
            <Icon name="Send" size={16} className="mr-2" />
            {newNotification.scheduled ? 'Schedule' : 'Send Now'}
          </Button>
        </div>
      </div>

      {/* Notifications List */}
      <div className="bg-white rounded-xl shadow-soft">
        <div className="p-6 border-b border-gray-200">
          <h3 className="font-heading font-semibold text-xl text-text-primary">
            Recent Notifications
          </h3>
        </div>

        <div className="p-6">
          <div className="space-y-4">
            {notifications.map((notification) => (
              <div key={notification.id} className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <h4 className="font-medium text-text-primary">{notification.title}</h4>
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(notification.status)}`}>
                        {notification.status}
                      </span>
                    </div>
                    <p className="text-text-secondary mb-2">{notification.message}</p>
                    <div className="flex items-center space-x-4 text-sm text-text-muted">
                      <span>Target: {notification.targetAudience}</span>
                      {notification.status === 'sent' && (
                        <>
                          <span>Sent: {notification.sentCount}</span>
                          <span>Click Rate: {notification.clickRate}%</span>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    {notification.status === 'scheduled' && (
                      <Button 
                        variant="primary" 
                        size="sm"
                        onClick={() => handleSendNotification(notification.id)}
                      >
                        Send Now
                      </Button>
                    )}
                    <Button variant="ghost" size="sm">
                      <Icon name="Edit" size={16} />
                    </Button>
                    <Button variant="ghost" size="sm">
                      <Icon name="Trash2" size={16} />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PushNotificationPanel;